import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import PropTypes from 'prop-types';
import { getAuthToken } from '../../../utils/auth';
import config from '../../../../config';
import CategoryTable from '../CategoryTable';
import Modal from '../../Modal';
import styles from './Category.module.css';
import modalStyles from '../ConfirmModal.module.css';
import useToggleMonetize from './use-toggleMonetize';
import useToggleActivation from './use-toggleActivation';

const getCategories = async (publisherId) => {
  const accessToken = getAuthToken();

  const response = await fetch(
    `${config.ApiBaseUrl}/publisher/${publisherId}/articleCategories`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  const json = await response.json();

  const { data, message, success } = json;

  if (!success)
    throw new Error(
      message ?? 'Something went wrong. failed to process request'
    );

  const retrievedCategories = data.articleCategories.map((item) => ({
    category: item.category,
    id: item.id,
    price: item.price,
    tipJarPrice: item.tipJarPriceOptions.prices,
  }));

  return retrievedCategories;
};

const CategoryIndex = () => {
  const [categories, setCategories] = useState([]);
  const [categoriesMap, setCategoriesMap] = useState(new Map());
  const [selectedCategories, setSelectedCategories] = useState(new Map());
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState('');
  const [request, setRequest] = useState({
    type: '',
    value: '',
  });
  const [clearSelection, setClearSelection] = useState(false);

  const publisherId = useSelector((state) => state.user.publisher?.id);

  useEffect(() => {
    if (publisherId) {
      (async () => {
        const pCategories = await getCategories(publisherId);
        setCategories(pCategories);

        const selectedMap = new Map();
        const pCategoriesMap = new Map();
        pCategories.forEach((category) => {
          // eslint-disable-next-line radix
          selectedMap.set(parseInt(category.id), false);
          // eslint-disable-next-line radix
          pCategoriesMap.set(parseInt(category.id), category.category);
        });
        // for (const category of pCategories) {
        //   selectedMap.set(parseInt(category.id, radix), false);
        //   pCategoriesMap.set(parseInt(category.id, radix), category.category);
        // }
        setCategoriesMap(pCategoriesMap);
        setSelectedCategories(selectedMap);
      })();
    }
  }, [publisherId]);

  const handleConfirmation = (type, value) => {
    if ([...selectedCategories.values()].find((x) => x === true)) {
      setShowModal(true);
      if (type === 'activation') {
        setModalText(
          `This will ${
            value ? 'activate' : 'de-activate'
          } all the articles in selected categories!`
        );
      } else {
        setModalText(
          `This will switch all articles in selected categories to ${value}`
        );
      }
      setRequest({
        type,
        value,
      });
    }
  };

  return (
    <>
      <div className={styles.category}>
        <div className={styles.actions}>
          <button
            type="button"
            className="global__button"
            onClick={() => handleConfirmation('activation', true)}
          >
            Activate
          </button>
          <button
            type="button"
            className="global__button global__button--state-danger"
            onClick={() => handleConfirmation('activation', false)}
          >
            Deactivate
          </button>
          <button
            type="button"
            className="global__button"
            onClick={() => {
              handleConfirmation('monetize', 'paywall');
            }}
          >
            Switch to paywall
          </button>
          <button
            type="button"
            className="global__button"
            onClick={() => {
              handleConfirmation('monetize', 'tipjar');
            }}
          >
            Switch to TipJar
          </button>
        </div>
        <CategoryTable
          categories={categories}
          setSelectedCategories={setSelectedCategories}
          clearSelection={clearSelection}
        />
        <Toaster
          containerStyle={{
            top: 50,
            left: 20,
            bottom: 20,
            right: 20,
          }}
        />
      </div>
      {showModal && (
        <ConfirmModal
          setShowModal={setShowModal}
          text={modalText}
          request={request}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          publisherId={publisherId}
          categoriesMap={categoriesMap}
          clearSelection={setClearSelection}
        />
      )}
    </>
  );
};

const ConfirmModal = ({
  setShowModal,
  text,
  request,
  selectedCategories,
  setSelectedCategories,
  categoriesMap,
  publisherId,
  clearSelection,
}) => {
  const handleActivation = useToggleActivation(selectedCategories, publisherId);
  const handleMonetize = useToggleMonetize(
    categoriesMap,
    selectedCategories,
    publisherId
  );

  useEffect(
    () =>
      function () {
        const clearSelectedCategories = selectedCategories;

        Array.from(clearSelectedCategories.keys()).forEach((key) => {
          clearSelectedCategories.set(key, false);
        });
        // for (const keys of clearSelectedCategories.keys()) {
        //   clearSelectedCategories.set(keys, false);
        // }
        setSelectedCategories(clearSelectedCategories);
        clearSelection(false);
      },
    []
  );

  const handleClick = (ok) => {
    if (ok) {
      if (request.type === 'activation') {
        handleActivation(request.value);
      } else {
        handleMonetize(request.value);
      }
      clearSelection(true);
    } else {
      clearSelection(true);
      console.log('user disagress');
    }

    setShowModal(false);
  };

  return (
    <Modal setShowModal={setShowModal} isLogin>
      <div className={modalStyles.container}>
        <p className={modalStyles.text}>{text}</p>
        <div className={modalStyles.actions}>
          <button
            type="button"
            className={`${modalStyles.button} ${modalStyles.success}`}
            onClick={() => {
              handleClick(true);
            }}
          >
            yes
          </button>
          <button
            type="button"
            className={`${modalStyles.button} ${modalStyles.error}`}
            onClick={() => {
              handleClick(false);
            }}
          >
            no
          </button>
        </div>
      </div>
    </Modal>
  );
};

ConfirmModal.propTypes = {
  setShowModal: PropTypes.node.isRequired,
  text: PropTypes.node.isRequired,
  request: PropTypes.node.isRequired,
  selectedCategories: PropTypes.node.isRequired,
  setSelectedCategories: PropTypes.node.isRequired,
  categoriesMap: PropTypes.node.isRequired,
  publisherId: PropTypes.node.isRequired,
  clearSelection: PropTypes.node.isRequired,
};

export default CategoryIndex;
